/**
 * Shared gate for AI-powered features.
 *
 * AI is operational when: provider !== 'none', API key is configured,
 * and the monthly spend limit has not been exceeded.
 */

import { useAiSettings } from './useAiSettings';
import { useUsageAnalytics } from './useUsageAnalytics';
import type { AiSettings } from '../types/ai-configuration';

export interface IsAiOperationalResult {
  isLoading: boolean;
  aiEnabled: boolean;
  settings: AiSettings | undefined;
  hasKey: boolean;
  providerEnabled: boolean;
  underLimit: boolean;
}

export function useIsAiOperational(userId?: string | null): IsAiOperationalResult {
  const { data: settings, isPending: settingsPending } = useAiSettings();
  const { data: usage } = useUsageAnalytics();

  const hasKey = !!(settings?.apiKeyMasked != null && settings.apiKeyMasked !== '');
  const providerEnabled = settings?.provider != null && settings.provider !== 'none';
  const limitCents = settings?.monthlySpendLimitCents ?? null;
  const underLimit =
    limitCents == null ||
    limitCents <= 0 ||
    (usage?.thisMonthCostCents ?? 0) < limitCents;

  const aiEnabled = !!(
    (userId === undefined || userId) &&
    settings &&
    providerEnabled &&
    hasKey &&
    underLimit
  );

  return {
    isLoading: settingsPending,
    aiEnabled,
    settings,
    hasKey,
    providerEnabled,
    underLimit,
  };
}
